import { useId, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BUILD, api, useApiQuery } from "../api";
import { cx } from "../components";
import { usePageEnter } from "../motion";
import styles from "./AppLayout.module.css";
import type { NavItem } from "./routes";
import { UserMenu } from "./UserMenu";

export type AppLayoutProps = {
  /** Les entrées de navigation de l'espace. */
  navItems: NavItem[];
  /** La marque en haut de la navigation : logo et nom. */
  brand: ReactNode;
  /** Surcharge de style de la coquille — l'accent d'une entreprise. */
  style?: CSSProperties;
};

/**
 * Coquille de l'application : navigation persistante à gauche, écran courant à
 * droite.
 *
 * Une seule coquille pour les deux espaces (entreprise, ESN), paramétrée par
 * ses entrées et sa marque plutôt que dupliquée : deux coquilles auraient
 * divergé au troisième jour. La navigation ne se démonte jamais d'un écran à
 * l'autre — c'est ce qui fait que passer du dashboard à l'historique ne
 * clignote pas.
 */
export function AppLayout({ navItems, brand, style }: AppLayoutProps) {
  // Sonde d'exploitation : une pastille verte prouve au premier coup d'œil, en
  // démonstration, que le front parle bien au back. Une pastille rouge évite
  // dix minutes passées à chercher un bogue d'écran alors que l'API est
  // éteinte.
  const sante = useApiQuery((o) => api.health(o), []);
  const enLigne = sante.data !== null && sante.error === null;
  // En largeur réduite, la navigation se replie derrière un bouton : une
  // rangée de liens qui défile de côté cachait la moitié des écrans.
  const [ouvert, setOuvert] = useState(false);
  const idPanneau = useId();
  // Un écran = les trois premiers segments du chemin : les onglets d'une fiche
  // d'entreprise (`/esn/entreprises/:id/…`) gardent leur en-tête immobile,
  // deux écrans de la navigation font chacun leur entrée.
  const { pathname } = useLocation();
  const page = usePageEnter<HTMLDivElement>(pathname.split("/").slice(0, 4).join("/"));

  return (
    <div className={styles.coquille} style={style}>
      <a className={styles.evitement} href="#contenu">
        Aller au contenu
      </a>

      <nav className={cx(styles.navigation, ouvert && styles.ouverte)} aria-label="Navigation principale">
        <div className={styles.barre}>
          <div className={styles.marque}>{brand}</div>
          <button
            type="button"
            className={styles.bascule}
            aria-expanded={ouvert}
            aria-controls={idPanneau}
            onClick={() => setOuvert((o) => !o)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              {ouvert ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
            Menu
          </button>
        </div>

        <div id={idPanneau} className={styles.panneau}>
          <ul className={styles.liens}>
            {navItems.map((item, i) => (
              <li key={item.to}>
                {item.group !== undefined && item.group !== navItems[i - 1]?.group && (
                  <span className={styles.groupe}>{item.group}</span>
                )}
                <NavLink
                  to={item.to}
                  end={item.end}
                  title={item.hint}
                  onClick={() => setOuvert(false)}
                  className={({ isActive }) =>
                    cx(styles.lien, isActive && styles.lienActif)
                  }
                >
                  {item.icon}
                  {item.label}
                  {item.count !== undefined && item.count > 0 && (
                    <span className={styles.compteur}>
                      {item.count}
                      <span className="lde-visually-hidden"> en attente</span>
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className={styles.piedNav}>
            <UserMenu />
            <span className={styles.etatService}>
              <span
                className={cx(
                  styles.pastilleEtat,
                  !sante.loading && (enLigne ? styles.enLigne : styles.horsLigne),
                )}
                aria-hidden="true"
              />
              {sante.loading
                ? "Connexion…"
                : enLigne
                  ? "Service en ligne"
                  : "Service injoignable"}
            </span>
            <span className={styles.build}>{BUILD}</span>
          </div>
        </div>
      </nav>

      <main id="contenu" className={styles.contenu}>
        <div ref={page} className={styles.page}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
